import dayjs from 'dayjs'
import { useAtom } from 'jotai/index'

import { useRoom } from 'src/hooks/useRoom'
import {
  RoomItemBasedFilteringItemType,
  RoomItemBasedFilteringKey,
  roomTableFilteringStore,
} from 'src/stores/roomTableFilteringStore'
import { ApplicationStatus, RoomEntity } from 'src/types'

type LoadFilteredRoomsInput<T extends Partial<RoomEntity>[]> = {
  rooms: T
  itemBasedFiltering: RoomItemBasedFilteringItemType | undefined
  rangeFilterRegisterLimitAt: { start?: Date; end?: Date } | undefined
  excludeKeys?: RoomItemBasedFilteringKey[]
}

type UpdateItemBasedFilteringInput = {
  key: RoomItemBasedFilteringKey
  values: string[] | undefined
}

type UpdateSelectMasterItemBasedFilteringInput = {
  selectMasterId: string
  values: string[] | undefined
}

type ToggleItemBasedFilteringInput = {
  key: RoomItemBasedFilteringKey
  targetValue: string
}

type ToggleSelectMasterItemBasedFilteringInput = {
  selectMasterId: string
  targetValue: string
}

export const useRoomTableFiltering = () => {
  const [storeValue, setStoreValue] = useAtom(roomTableFilteringStore.state)

  const NoSetText = 'なし'

  const {
    loadApplicationStatus,
    loadRegistrationStatus,
    loadApplicationStatusText,
    loadRegistrationStatusText,
  } = useRoom()

  const setSearchWord = (searchWord: string | undefined) => {
    setStoreValue(previous => ({
      ...previous,
      searchWord,
    }))
  }

  const setFilteredRoomIds = (filteredRoomIds: string[] | undefined) => {
    setStoreValue(previous => ({
      ...previous,
      filteredRoomIds,
    }))
  }

  const setItemBasedFiltering = (
    itemBasedFiltering: RoomItemBasedFilteringItemType | undefined
  ) => {
    setStoreValue(previous => ({
      ...previous,
      itemBasedFiltering,
    }))
  }

  // 住戸一覧のフィルタリング状態の特定のkeyの値を更新する。セレクト設問は別関数で対応する
  const updateItemBasedFiltering = ({ key, values }: UpdateItemBasedFilteringInput) => {
    setStoreValue(previous => ({
      ...previous,
      itemBasedFiltering: {
        ...previous.itemBasedFiltering,
        [key]: values,
      },
    }))
  }

  const updateSelectMasterItemBasedFiltering = (
    input: UpdateSelectMasterItemBasedFilteringInput
  ) => {
    const { selectMasterId, values } = input
    setStoreValue(previous => {
      const previousSelectMasters = previous.itemBasedFiltering?.selectMasters

      const targetSelectMaster = previousSelectMasters?.find(
        selectMaster => selectMaster.id === selectMasterId
      )
      const selectMasters = targetSelectMaster
        ? previousSelectMasters?.map(selectMaster => {
            if (selectMaster.id !== selectMasterId) {
              return selectMaster
            }

            return {
              id: selectMasterId,
              selectedTexts: values,
            }
          })
        : previousSelectMasters
        ? [...previousSelectMasters, { id: selectMasterId, selectedTexts: values }]
        : [{ id: selectMasterId, selectedTexts: values }]

      return {
        ...previous,
        itemBasedFiltering: {
          ...previous.itemBasedFiltering,
          selectMasters,
        },
      }
    })
  }

  // 住戸一覧のフィルタリング状態の特定のkey、特定の値をトグルする。セレクト設問は別関数で対応する
  const toggleItemBasedFiltering = ({ key, targetValue }: ToggleItemBasedFilteringInput) => {
    if (key === 'selectMasters') {
      return
    }

    setStoreValue(previous => {
      if (!previous.itemBasedFiltering) {
        return {
          ...previous,
          itemBasedFiltering: {
            [key]: [targetValue],
          },
        }
      }

      const values = previous.itemBasedFiltering[key]
      const updatedValues = values?.includes(targetValue)
        ? // 既に存在する場合は取り除く
          values.filter(value => value !== targetValue)
        : values
        ? // 存在しない場合は追加
          [...values, targetValue]
        : // 存在しない場合は追加、ただしvaluesがundefinedの場合はスプレッド構文が使えない
          [targetValue]

      return {
        ...previous,
        itemBasedFiltering: {
          ...previous.itemBasedFiltering,
          [key]: updatedValues,
        },
      }
    })
  }

  const toggleSelectMasterItemBasedFiltering = (
    input: ToggleSelectMasterItemBasedFilteringInput
  ) => {
    const { selectMasterId, targetValue } = input

    setStoreValue(previous => {
      if (!previous.itemBasedFiltering) {
        return {
          ...previous,
          itemBasedFiltering: {
            selectMasters: [
              {
                id: selectMasterId,
                selectedTexts: [targetValue],
              },
            ],
          },
        }
      }

      const { selectMasters } = previous.itemBasedFiltering
      const updatedSelectMasters = selectMasters?.map(selectMaster => {
        if (selectMaster.id !== selectMasterId) {
          return selectMaster
        }

        const { selectedTexts } = selectMaster
        const updatedSelectedTexts = selectedTexts?.includes(targetValue)
          ? // 既に存在する場合は取り除く
            selectedTexts.filter(value => value !== targetValue)
          : selectedTexts
          ? // 存在しない場合は追加
            [...selectedTexts, targetValue]
          : // 存在しない場合は追加、ただしselectedTextsがundefinedの場合はスプレッド構文が使えない
            [targetValue]

        return {
          id: selectMasterId,
          selectedTexts: updatedSelectedTexts,
        }
      })

      return {
        ...previous,
        itemBasedFiltering: {
          ...previous.itemBasedFiltering,
          selectMasters: updatedSelectMasters,
        },
      }
    })
  }

  const resetSelectMasterItemBasedFiltering = ({ selectMasterId }: { selectMasterId: string }) => {
    setStoreValue(previous => {
      if (!previous.itemBasedFiltering) {
        return previous
      }

      const { selectMasters } = previous.itemBasedFiltering
      const updatedSelectMasters = selectMasters?.filter(
        selectMaster => selectMaster.id !== selectMasterId
      )

      return {
        ...previous,
        itemBasedFiltering: {
          ...previous.itemBasedFiltering,
          selectMasters:
            updatedSelectMasters && updatedSelectMasters.length > 0
              ? updatedSelectMasters
              : undefined,
        },
      }
    })
  }

  const setRangeFilterRegisterLimitAt = (input: { start?: Date; end?: Date } | undefined) => {
    setStoreValue(previous => ({
      ...previous,
      rangeFilterRegisterLimitAt: input,
    }))
  }

  const resetFilteringItems = () => {
    setSearchWord(undefined)
    setFilteredRoomIds(undefined)
    setItemBasedFiltering(undefined)
    setRangeFilterRegisterLimitAt(undefined)
  }

  const loadFilteredRooms = <T extends Partial<RoomEntity>[]>(
    input: LoadFilteredRoomsInput<T>
  ): T => {
    const { rooms, itemBasedFiltering, rangeFilterRegisterLimitAt, excludeKeys } = input
    const { searchWord, filteredRoomIds } = storeValue

    let targets: Partial<RoomEntity>[] | RoomEntity[] = rooms

    if (searchWord) {
      targets = rooms.filter(room => {
        const publishApplicationStatusText =
          room.publishApplicationRequestStatus &&
          loadApplicationStatusText(room.publishApplicationRequestStatus)
        const orderApplicationStatusText =
          room.orderApplicationRequestStatus &&
          loadApplicationStatusText(room.orderApplicationRequestStatus)
        const registrationStatusText =
          room.registrationStatus && loadRegistrationStatusText(room.registrationStatus)

        return (
          room.name?.includes(searchWord) ||
          room.wing?.name?.includes(searchWord) ||
          room.floor?.includes(searchWord) ||
          room.roomType?.name?.includes(searchWord) ||
          (room.isSelectEnabled ? '' : '対象外').includes(searchWord) ||
          room.memo?.includes(searchWord) ||
          publishApplicationStatusText?.includes(searchWord) ||
          room.contractor?.name?.includes(searchWord) ||
          (room.contractor?.isConfirmed ? '確認済' : '').includes(searchWord) ||
          (room.contractor?.selectEmailSentTime
            ? `${room.contractor.selectEmailSentTime}回`
            : ''
          ).includes(searchWord) ||
          (room.contractor?.agreementRequestEmailSentTime
            ? `${room.contractor.agreementRequestEmailSentTime}回`
            : ''
          ).includes(searchWord) ||
          registrationStatusText?.includes(searchWord) ||
          orderApplicationStatusText?.includes(searchWord)
        )
      })
    }

    if (filteredRoomIds) {
      targets = targets.filter(room => {
        return room.id && filteredRoomIds.includes(room.id)
      })
    }

    if (itemBasedFiltering) {
      const {
        names,
        wingNames,
        floors,
        roomTypes,
        selectEnabledItems,
        memos,
        selectMasters,
        publishApplications,
        orderApplications,
        contractorNames,
        selectProxyRegisters,
        contractorConfirmed,
        selectEmailSentTimes,
        agreementRequestEmailSentTime,
        registrationStatuses,
        registerLimitAts,
      } = itemBasedFiltering

      if (!excludeKeys?.includes('names') && names) {
        targets = targets.filter(room => {
          return room?.name && names.includes(room.name)
        })
      }
      if (!excludeKeys?.includes('wingNames') && wingNames) {
        targets = targets.filter(room => {
          return room?.wing?.name && wingNames.includes(room.wing.name)
        })
      }
      if (!excludeKeys?.includes('floors') && floors) {
        targets = targets.filter(room => {
          return room?.floor && floors.includes(room.floor)
        })
      }
      if (!excludeKeys?.includes('roomTypes') && roomTypes) {
        targets = targets.filter(room => {
          return room?.roomType?.name && roomTypes.includes(room.roomType.name)
        })
      }
      if (!excludeKeys?.includes('selectEnabledItems') && selectEnabledItems) {
        // 空配列（チェックが全て外れている）の場合は0件表示
        if (selectEnabledItems.length === 0) {
          targets = []
        }
        if (selectEnabledItems.length === 1) {
          const condition = selectEnabledItems[0] === NoSetText
          targets = targets.filter(room => room?.isSelectEnabled === condition)
        }
      }
      if (!excludeKeys?.includes('memos') && memos) {
        targets = targets.filter(room => {
          return (
            // 未設定が選択されている場合、memoが存在しないものを抽出
            (memos.includes(NoSetText) && !room.memo) ||
            // それ以外の選択肢の場合は選択されているmemoを持つ住戸を抽出
            (room?.memo && memos.includes(room.memo))
          )
        })
      }
      if (!excludeKeys?.includes('selectMasters') && selectMasters) {
        targets = targets.filter(room => {
          return selectMasters.every(selectMasterFilteringItem => {
            const { id, selectedTexts } = selectMasterFilteringItem
            // TODO: 紐づけ済・紐づけなしの2択のため、length === 2 としているがマジックナンバーになってしまうので修正したい
            if (!selectedTexts || selectedTexts.length === 2) {
              return true
            }
            if (selectedTexts.length === 0) {
              return false
            }

            // 紐づけ済・紐づけなし どちらかが選択されている場合
            // 紐づけ済の場合は選択されていれば true を返し、紐づけなしの場合は選択されていなければ true を返す
            const v = room.select?.selectedItemIds.includes(id)
            return selectedTexts.includes('紐づけ済') ? v : !v
          })
        })
      }
      if (!excludeKeys?.includes('publishApplications') && publishApplications) {
        // 空配列（チェックが全て外れている）の場合は0件表示
        if (publishApplications.length === 0) {
          targets = []
        }
        const selectedStatuses = new Set(
          publishApplications
            .map(s => loadApplicationStatus(s))
            .filter((status): status is ApplicationStatus => !!status)
        )
        targets = targets.filter(
          room =>
            room.publishApplicationRequestStatus &&
            selectedStatuses.has(room.publishApplicationRequestStatus)
        )
      }
      if (!excludeKeys?.includes('orderApplications') && orderApplications) {
        // 空配列（チェックが全て外れている）の場合は0件表示
        if (orderApplications.length === 0) {
          targets = []
        }
        const selectedStatuses = new Set(
          orderApplications
            .map(s => loadApplicationStatus(s))
            .filter((status): status is ApplicationStatus => !!status)
        )
        targets = targets.filter(
          room =>
            room.orderApplicationRequestStatus &&
            selectedStatuses.has(room.orderApplicationRequestStatus)
        )
      }
      if (!excludeKeys?.includes('contractorNames') && contractorNames) {
        targets = targets.filter(room => {
          return (
            // 未設定が選択されている場合、契約者が存在しないものを抽出
            (contractorNames.includes(NoSetText) && !room.contractor?.name) ||
            // それ以外の選択肢の場合は選択されている契約者を持つ住戸を抽出
            (room.contractor?.name && contractorNames.includes(room.contractor.name))
          )
        })
      }
      if (!excludeKeys?.includes('contractorConfirmed') && contractorConfirmed) {
        // 空配列（チェックが全て外れている）の場合は0件表示
        if (contractorConfirmed.length === 0) {
          targets = []
        }
        if (contractorConfirmed.length === 1) {
          const condition = contractorConfirmed[0] === '確認済'
          targets = targets.filter(room => {
            const baseCondition = !!room.contractor?.isConfirmed
            return condition ? baseCondition : !baseCondition
          })
        }
      }
      if (!excludeKeys?.includes('selectEmailSentTimes') && selectEmailSentTimes) {
        // 空配列（チェックが全て外れている）の場合は0件表示
        if (selectEmailSentTimes.length === 0) {
          targets = []
        }
        const times = new Set(selectEmailSentTimes.map(time => Number(time.replace('回', ''))))

        targets = targets.filter(room => {
          // 未設定が選択されている場合、案内通知が存在しないものを抽出
          if (selectEmailSentTimes.includes(NoSetText) && !room.contractor?.selectEmailSentTime) {
            return true
          }

          if (!room.contractor?.selectEmailSentTime) {
            return false
          }
          return times.has(room.contractor?.selectEmailSentTime)
        })
      }
      if (
        !excludeKeys?.includes('agreementRequestEmailSentTime') &&
        agreementRequestEmailSentTime
      ) {
        // 空配列（チェックが全て外れている）の場合は0件表示
        if (agreementRequestEmailSentTime.length === 0) {
          targets = []
        }
        const times = new Set(
          agreementRequestEmailSentTime.map(time => Number(time.replace('回', '')))
        )

        targets = targets.filter(room => {
          // 未設定が選択されている場合、同意依頼通知が存在しないものを抽出
          if (
            agreementRequestEmailSentTime.includes(NoSetText) &&
            !room.contractor?.agreementRequestEmailSentTime
          ) {
            return true
          }

          if (!room.contractor?.agreementRequestEmailSentTime) {
            return false
          }
          return times.has(room.contractor?.agreementRequestEmailSentTime)
        })
      }
      if (!excludeKeys?.includes('selectProxyRegisters') && selectProxyRegisters) {
        // 空配列（チェックが全て外れている）の場合は0件表示
        if (selectProxyRegisters.length === 0) {
          targets = []
        }
        if (selectProxyRegisters.length === 1) {
          const condition = selectProxyRegisters[0] === '登録可'
          targets = targets.filter(room => {
            const baseCondition = room.isEnableProxySelectRegistration
            return condition ? baseCondition : !baseCondition
          })
        }
      }
      if (!excludeKeys?.includes('registrationStatuses') && registrationStatuses) {
        // 空配列（チェックが全て外れている）の場合は0件表示
        if (registrationStatuses.length === 0) {
          targets = []
        }
        const targetStatuses = new Set(registrationStatuses.map(s => loadRegistrationStatus(s)))
        targets = targets.filter(room => {
          return (
            // 未設定が選択されている場合、登録状況が未登録のものを抽出
            (registrationStatuses.includes(NoSetText) &&
              (!room.registrationStatus || room.registrationStatus === 'waiting')) ||
            // それ以外の選択肢の場合は選択されている登録状況を持つ住戸を抽出
            targetStatuses.has(room.registrationStatus)
          )
        })
      }
      if (!excludeKeys?.includes('registerLimitAts') && registerLimitAts) {
        // 空配列（チェックが全て外れている）の場合は0件表示
        if (registerLimitAts.length === 0) {
          targets = []
        }
        targets = targets.filter(room => {
          return (
            // 未設定が選択されている場合、契約者同意期限が存在しないものを抽出
            (registerLimitAts.includes(NoSetText) && !room.select?.registerLimitAt) ||
            // それ以外の選択肢の場合は選択されている契約者同意期限を持つ住戸を抽出
            registerLimitAts.some(registerLimitAt => {
              const targetAt = dayjs(room.select?.registerLimitAt).format('YY/MM/DD')
              return registerLimitAt === targetAt
            })
          )
        })
      }
    }

    if (rangeFilterRegisterLimitAt) {
      const { start, end } = rangeFilterRegisterLimitAt
      if (start) {
        targets = targets.filter(room => {
          if (!room.select?.registerLimitAt) {
            return false
          }
          const targetAt = dayjs(room.select.registerLimitAt).format('YY/MM/DD')
          const startAt = dayjs(start).format('YY/MM/DD')
          return targetAt >= startAt
        })
      }
      if (end) {
        targets = targets.filter(room => {
          if (!room.select?.registerLimitAt) {
            return false
          }
          const targetAt = dayjs(room.select.registerLimitAt).format('YY/MM/DD')
          const endAt = dayjs(end).format('YY/MM/DD')
          return targetAt <= endAt
        })
      }
    }

    return targets as T
  }

  return {
    searchWord: storeValue.searchWord,
    setSearchWord,

    filteredRoomIds: storeValue.filteredRoomIds,
    setFilteredRoomIds,

    itemBasedFiltering: storeValue.itemBasedFiltering,
    setItemBasedFiltering,
    updateItemBasedFiltering,
    updateSelectMasterItemBasedFiltering,
    toggleItemBasedFiltering,
    toggleSelectMasterItemBasedFiltering,
    resetSelectMasterItemBasedFiltering,

    rangeFilterRegisterLimitAt: storeValue.rangeFilterRegisterLimitAt,
    setRangeFilterRegisterLimitAt,

    resetFilteringItems,

    loadFilteredRooms,

    NoSetText,
  }
}
